import React from "react";
import { Helmet } from "react-helmet-async";

// Per-area SEO head + crawler-readable fact block for /dubai-market/:slug (moved from the retired MarketWidgets section).
const fmtInt = (n, isAR = false) => (n == null ? "—" : new Intl.NumberFormat(isAR ? "ar-EG" : "en-US", { maximumFractionDigits: 0 }).format(n).replace(/\u066C/g, ","));
const toSlug = (name) => (name || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function AreaSeoHead({ selected, urlSlug, invalidSlug, lang }) {
  if (invalidSlug) {
    return (
      <Helmet>
        <title>{lang === "ar" ? "المنطقة غير موجودة · بروبيرتك" : "Community not found · The Propertic"}</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
    );
  }
  if (!urlSlug || !selected) return null;
  const canonicalUrl = `https://thepropertic.ae/dubai-market/${toSlug(selected.area)}`;
  const isPos = selected.yoy_change_pct >= 0;
  const areaAsOf = selected.as_of || "";
  const areaLoc = lang === "ar" ? selected.area_ar || selected.area : selected.area;
  const canonicalHref = lang === "ar" ? `${canonicalUrl}?lang=ar` : canonicalUrl;
  const title = lang === "ar" ? `${areaLoc} · أسعار دبي وعوائد الإيجار · بروبيرتك` : `${areaLoc} · Dubai prices & rental yields · The Propertic`;
  const description = lang === "ar"
    ? `${areaLoc} في دبي: متوسط سعر القدم²، عائد الإيجار، صفقات ٢٠٢٦ حتى اليوم، ونسبة قيد الإنشاء مقابل الجاهز. المصدر: دائرة الأراضي والأملاك.`
    : `${areaLoc}, Dubai · AED/sqft, gross rental yield, YTD 2026 transactions and off-plan vs ready split. Source: Dubai Land Department Open Data.`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "BreadcrumbList", "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": lang === "ar" ? "الرئيسية" : "Home", "item": "https://thepropertic.ae/" },
        { "@type": "ListItem", "position": 2, "name": lang === "ar" ? "سوق دبي العقاري" : "Dubai Property Market", "item": "https://thepropertic.ae/dubai-market" },
        { "@type": "ListItem", "position": 3, "name": lang === "ar" ? selected.area_ar : selected.area, "item": canonicalUrl },
      ] },
      { "@type": "WebPage", "name": lang === "ar" ? `${selected.area_ar} — أسعار وعوائد دبي` : `${selected.area} — Dubai Prices & Yields`, "url": canonicalUrl,
        "about": { "@type": "Place", "name": selected.area, "containedInPlace": { "@type": "AdministrativeArea", "name": "Dubai" } },
        "isPartOf": { "@id": "https://thepropertic.ae/#website" } },
      { "@type": "Dataset", "name": `${selected.area} — Dubai property market metrics`,
        "description": `AED/sqft, gross rental yield, 1BR/2BR rent, YTD 2026 transaction count and YoY change for ${selected.area}, Dubai. Sourced from Dubai Land Department Open Data Gateway; refreshed weekly.`,
        "url": canonicalUrl,
        "creator": { "@type": "Organization", "name": "The Propertic (Provest Real Estate LLC)" },
        "publisher": { "@type": "Organization", "name": "The Propertic (Provest Real Estate LLC)", "url": "https://thepropertic.ae" },
        "sourceOrganization": { "@type": "GovernmentOrganization", "name": "Dubai Land Department", "url": "https://dubailand.gov.ae" },
        "temporalCoverage": "2026", "spatialCoverage": { "@type": "Place", "name": `${selected.area}, Dubai, United Arab Emirates` },
        "license": "https://dubailand.gov.ae", "isAccessibleForFree": true, ...(areaAsOf ? { "dateModified": areaAsOf } : {}) },
    ],
  };
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalHref} />
        <link rel="alternate" hrefLang="en" href={canonicalUrl} />
        <link rel="alternate" hrefLang="ar" href={`${canonicalUrl}?lang=ar`} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalHref} />
        <meta property="og:image" content="https://thepropertic.ae/og-share-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <div className="sr-only" data-testid="area-fact-block">
        <h2>{lang === "ar" ? `${selected.area_ar} — دبي` : `${selected.area} — Dubai`}</h2>
        <ul>
          <li><strong>{lang === "ar" ? "مؤشّر سعر القدم المربّعة المبني على قيم الوسيط" : "Median-based price/sq ft indicator"}:</strong> AED {fmtInt(selected.avg_price_sqft_aed)}/sqft</li>
          <li><strong>{lang === "ar" ? "العائد الإجمالي" : "Gross rental yield"}:</strong> {selected.rental_yield_pct}%</li>
          <li><strong>{lang === "ar" ? "إيجار غرفة" : "1BR annual rent"}:</strong> AED {fmtInt(selected.avg_rent_1br_aed)}</li>
          <li><strong>{lang === "ar" ? "مبيعات مسجّلة ٢٠٢٦" : "YTD 2026 registered sales"}:</strong> {fmtInt(selected.transactions_last_quarter)}</li>
          <li><strong>{lang === "ar" ? "تغير سنوي" : "YoY change"}:</strong> {isPos ? "+" : ""}{selected.yoy_change_pct}%</li>
          <li><strong>{lang === "ar" ? "المصدر" : "Source"}:</strong> DLD Open Data{areaAsOf ? ` · ${areaAsOf}` : ""}</li>
        </ul>
      </div>
    </>
  );
}
